import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import { FC, JSX, useContext, useEffect, useRef, useState } from "react";

import { ExportData } from "@app/exports";
import { NotFound } from "@app/shared/ErrorPages";
import {
  CloneProject,
  CreateProjectWizard,
  CountyProjectOverview,
  ProjectEditRouter,
} from "./";
import BulkProjectEdit from "./edit/BulkProjectEdit";
import { ResourceService, ResourceType } from "@app/resources";
import { LoadingSpinner } from "@app/shared";
import ZipProjectOverview from "./zip-projects/ZipProjectOverview";
import { ReportCustomization } from "@app/report-customization/build-reports/ReportCustomization";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { FeatureFlagContext, Features } from "@app/feature-flags";

type ProjectsRouterProps = RouteComponentProps<undefined, undefined, ExportDataRouteState>;

/** Renders the routing components for the projects sub-section of the app. */
export default function ProjectsRouter(props: ProjectsRouterProps): JSX.Element {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  return (
    <Switch>
      <Route path={`${props.match.path}/create`} component={CreateProjectWizard} />
      <Route path={`${props.match.path}/:projectId/clone`} component={CloneProject} />
      <Route path={`${props.match.path}/:projectId/edit`} component={ProjectEditRouter} />


      {props.location.pathname.includes("/export") && !props.location.state &&
        <Redirect from={`${props.match.path}/:projectId/:module/export/:runConfigurationId/:isLatestPublished`} to={`${props.match.path}/:projectId`} />}

      {/* new create report page */}
      {/* Unfortunately props.match.params isn't populated until after the route is chosen, so we have to rely on the pathname */}
      {
        featureFlags.has(Features.ADEQUACY_REPORT_CUSTOMIZATION) &&
        props.location.pathname.includes("adequacy") && (
          <Route path={`${props.match.path}/:projectId/:module/export/:runConfigurationId/:isLatestPublished`} component={ExportsProjectTypeLoader} />
        )
      }
      
      {/* old create report page */}
      <Route exact={true} path={`${props.match.path}/:projectId/:module/export/:runConfigurationId/:isLatestPublished`} component={ExportData} />
      {props.location.pathname.includes("/export") && !props.location.state &&
        <Redirect
          from={`${props.match.path}/:projectId/:module/:runConfigurationId/:scope/:networkType/:resultType/:key/export`}
          to={`${props.match.path}/:projectId/:module/:runConfigurationId/:scope/:networkType/:resultType/:key`}
        />
      }
      <Route path={`${props.match.path}/:projectId/:module/:runConfigurationId/:scope/:networkType/:resultType/:key/export`} component={ExportData} />

      <Redirect push={false} strict={true} exact={true} from={`${props.match.path}/:projectId/`} to={{ pathname: `${props.match.path}/:projectId`, state: props.location.state }} />
      <Route path={`${props.match.path}/edit`} component={BulkProjectEdit} />
      {/* trailing a parameter with ? makes it optional */}
      <Route path={`${props.match.path}/:projectId/:module?`} component={ProjectTypeLoader} /> {/*This needs to be the last route*/}
      <Route component={NotFound} />
    </Switch>
  );
}

const ProjectTypeLoader: FC<RouteComponentProps<{ projectId: string }>> = (props: RouteComponentProps<{ projectId: string }>) => {
  const [resourceType, setResourceType] = useState<ResourceType>();
  const resourceService = useRef(new ResourceService()).current;
  const params: { projectId: string } = props.match.params as { projectId: string };

  useEffect(() => {
    resourceService.getResourceType(parseInt(params.projectId, 10))
      .then((response) => {
        setResourceType(response);
      });
  }, [params.projectId]);

  switch (resourceType) {
    case undefined:
      return (
        <div className="mt-3 ml-3">
          <LoadingSpinner />
        </div>
      );
    case ResourceType.CustomProject:
      return <CountyProjectOverview {...props} />;
    case ResourceType.ZipAnalysisProject:
      return <ZipProjectOverview />;
  }
}

// this function should be temporary, until zip projects support report customization
/* eslint-disable @typescript-eslint/no-explicit-any */
const ExportsProjectTypeLoader: FC<RouteComponentProps<any, undefined, unknown>> = (props: RouteComponentProps<any, undefined, unknown>) => {
  const [resourceType, setResourceType] = useState<ResourceType>();
  const resourceService = useRef(new ResourceService()).current;
  const params: { projectId: string } = props.match.params as { projectId: string };

  useEffect(() => {
    resourceService.getResourceType(parseInt(params.projectId, 10))
      .then((response) => {
        setResourceType(response);
      });
  }, [params.projectId]);

  switch (resourceType) {
    case undefined:
      return (
        <div className="mt-3 ml-3">
          <LoadingSpinner />
        </div>
      );
    case ResourceType.CustomProject:
      return <ReportCustomization />;
    case ResourceType.ZipAnalysisProject:
      return <ExportData {...props} />;
  }
}
